import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  CIRCUIT_HIGHLIGHT_COLORS,
  CIRCUIT_HIGHLIGHT_CYCLE,
} from '@/components/climbs/board-grid/roles';
import type { CircuitHighlightColor, LedDirection, LedOrigin } from '../../../types/device';

const LED_ORIGINS: LedOrigin[] = ['top_left', 'top_right', 'bottom_left', 'bottom_right'];
const LED_DIRECTIONS: LedDirection[] = [
  'row_progressive',
  'row_serpentine',
  'column_progressive',
  'column_serpentine',
];

/** What the panel holds for a wired wall's LEDs while they are being edited. */
export interface LedSettings {
  /** As typed: empty leaves the strip on whatever brightness it has. */
  brightness: string;
  highlightColor: CircuitHighlightColor;
  origin: LedOrigin | '';
  direction: LedDirection | '';
}

interface BoardLedSettingsProps {
  value: LedSettings;
  onChange: (next: LedSettings) => void;
  /**
   * Where the strip starts and how it snakes: a grid's LEDs are its rows and
   * columns walked, so only a grid asks. A spray wall's follow its LED strips.
   */
  wiring?: boolean;
}

/**
 * **How a wired wall's LEDs look**, set on the board's page next to the wall
 * they light: the strip's brightness, the colour a circuit's current hold is
 * lit in and — on a grid — how the strip runs. Only on a wall with a device:
 * there is nothing to light otherwise.
 */
export function BoardLedSettings({ value, onChange, wiring = false }: BoardLedSettingsProps) {
  const { t } = useTranslation();
  const set = (patch: Partial<LedSettings>) => onChange({ ...value, ...patch });

  return (
    <div className="space-y-3 rounded-md border p-3" data-testid="board-led-settings">
      <p className="text-xs font-medium">{t('gymAdmin.boardDashboard.grid.leds.title')}</p>

      {wiring && (
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">{t('gymAdmin.boards.fields.ledOrigin')}</Label>
            <Select value={value.origin} onValueChange={(origin) => set({ origin: origin as LedOrigin })}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                {LED_ORIGINS.map((origin) => (
                  <SelectItem key={origin} value={origin}>
                    {t(`gymAdmin.boards.ledOrigins.${origin}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t('gymAdmin.boards.fields.ledDirection')}</Label>
            <Select
              value={value.direction}
              onValueChange={(direction) => set({ direction: direction as LedDirection })}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                {LED_DIRECTIONS.map((direction) => (
                  <SelectItem key={direction} value={direction}>
                    {t(`gymAdmin.boards.ledDirections.${direction}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <div className="space-y-1">
        <Label htmlFor="board-led-brightness" className="text-xs">
          {t('gymAdmin.boards.fields.ledBrightness')}
        </Label>
        <Input
          id="board-led-brightness"
          type="number"
          min={0}
          max={255}
          className="h-9"
          value={value.brightness}
          placeholder="—"
          onChange={(event) => set({ brightness: event.target.value })}
        />
        <p className="text-xs text-muted-foreground">{t('gymAdmin.boards.fields.ledBrightnessHelp')}</p>
      </div>

      {/* The hold the climber is heading for during a circuit is lit in it,
          so it is picked out of a problem that is lit whole. */}
      <div className="space-y-1">
        <Label className="text-xs">{t('gymAdmin.boards.fields.circuitHighlightColor')}</Label>
        <Select
          value={value.highlightColor}
          onValueChange={(color) => set({ highlightColor: color as CircuitHighlightColor })}
        >
          <SelectTrigger className="h-9" aria-label={t('gymAdmin.boards.fields.circuitHighlightColor')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CIRCUIT_HIGHLIGHT_CYCLE.map((color) => (
              <SelectItem key={color} value={color}>
                <span className="flex items-center gap-2">
                  <span
                    className="size-3 rounded-full border"
                    style={{ backgroundColor: CIRCUIT_HIGHLIGHT_COLORS[color] }}
                    aria-hidden
                  />
                  {t(`gymAdmin.boards.circuitHighlightColors.${color}`)}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {t('gymAdmin.boards.fields.circuitHighlightColorHelp')}
        </p>
      </div>
    </div>
  );
}
